import { AnimatePresence, motion } from 'framer-motion';
import React, { Dispatch, Fragment, PureComponent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { MedicalHistoryConfig } from '../../../../../api/pushGenie';
import { capitalizeFirstLetter } from '../../../../../pages/utils';
import { Actions } from '../../../../../store/actions';
import {
    clearMedicalHistory,
    deleteMedicalHistory,
    swapMedicalHistoryElement,
    toggleNoKnownMedicalHistory,
    upsertMedicalHistory,
    upsertMedicalHistoryCardState,
} from '../../../../../store/actions/prescriptionPad';
import { GlobalState } from '../../../../../store/reducers';
import {
    getMedicalHistoryCardState,
    getNoKnownMedicalHistoryState,
    getPatientMedicalHistory,
    getPrescription,
} from '../../../../../store/selectors/prescriptionPad';
import { MEDICAL_HISTORY_CATEGORY, MHC, MHItem2 } from '../../../../../store/types/medicalHistory';
import { bottonArrowIcon, MedicalHistory, topArrowIcon } from '../../../../common/Icons';
import { medicalHistoryOptions } from '../../../../medicalHistoryConfigPanel/CustomiseSections';
import PPMedicalHistoryConfigPanel from './MedicalHistoryConfigPanel';
import MedicalHistoryItemsPadV2 from './MedicalHistoryItemsPadV2';
import PadV2PxElementCard from './PadV2PxElementCard';
import { MEDICAL_HISTORY_CONTAINER } from '../MedicalHistoryContainer';
import { DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES } from '../../../../../constants';
import { MISC_KEYS, getItemFromMisc } from '../../../../../store/idbHandler';
import { getMyIdWithBackwardCompatibility } from '../../../../../api/mactiveid';

type MedicalHistoryCardPadV2State = {
    showMedicalHistoryDetails: boolean;
    isOpen: boolean;
    medicalHistoryConfigData: MedicalHistoryConfig;
};

interface MedicalHistoryCardPadV2Props {
    dispatch: Dispatch<Actions>;
    isMedicalHistorySection?: boolean;
    noKnownMedicalHistoryState: boolean;
    medicalHistory?: MHC;
    cardState: boolean;
    sectionName?: string;
    rxDocId: string;
}

const MedicalHistoryIcon = MedicalHistory();

const EmptyMedicalHistoryMessageUI = () => {
    return (
        <p className="pt-16 text-darwin-neutral-600 font-400 text-14 select-none ">
            No quick options to show. please &ldquo;Configure &rdquo; your Medical History.
        </p>
    );
};

function mergeSummary(item: string | undefined, isFirstElement: { flag: number }) {
    if (!item) {
        return '';
    }
    if (isFirstElement.flag) {
        isFirstElement.flag = 0;
        return `- ${item}`;
    }
    return `:${item}`;
}

const getCodedStatus = (status: string | undefined): string => {
    if (!status) {
        return '';
    }
    if (status.toLowerCase() === 'active') return 'Yes';
    else if (status.toLowerCase() === 'inactive') return 'No';
    return '';
};

export function MedicalHistorySummary(medicalHistory: MHC): JSX.Element {
    const MHResults = Object.entries(medicalHistoryOptions).map((key) => {
        if (
            key[0] === MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY ||
            key[0] === MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY
        ) {
            return null;
        }

        const results = medicalHistory[key[0] as keyof MHC]
            ?.map((mh) => {
                const isFirstElement = {
                    flag: 1,
                };
                // is intensional if it is undefined than that column summary wont get added
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const { name, status, since, frequency, who } = mh;
                const coded_status = getCodedStatus(status);
                return status
                    ? `${capitalizeFirstLetter(name)} (${capitalizeFirstLetter(
                          coded_status,
                      )}) ${mergeSummary(since?.custom, isFirstElement)}${mergeSummary(
                          frequency?.custom,
                          isFirstElement,
                      )}${mergeSummary(who, isFirstElement)}`
                    : false;
            })
            .filter((mh) => mh !== false);

        return results?.length ? (
            <div key={key[0]} className="flex mb-2">
                <p className="body-1 text-neutral-black whitespace-nowrap font-700">
                    {medicalHistoryOptions[key[0] as keyof MHC].name}:
                </p>
                <p className="body-1 text-neutral-black ml-2">{results?.join(', ')}.</p>
            </div>
        ) : null;
    });

    const MHSummary =
        MHResults.filter((ele) => ele !== null).length > 0 ? (
            <div className="space-y-8">{MHResults}</div>
        ) : (
            <p className="text-darwin-neutral-600 font-400 text-14 select-none">
                No Medical History found
            </p>
        );

    return (
        <div className="flex flex-col justify-start items-start flex-wrap  bg-white">
            {MHSummary}
        </div>
    );
}

class MedicalHistoryCardPadV2 extends PureComponent<
    MedicalHistoryCardPadV2Props,
    MedicalHistoryCardPadV2State
> {
    state: MedicalHistoryCardPadV2State = {
        showMedicalHistoryDetails: true,
        isOpen: true,
        medicalHistoryConfigData: DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
    };

    componentDidMount() {
        this.setState({ isOpen: this.props.cardState });
        this.setState({ showMedicalHistoryDetails: !this.props.noKnownMedicalHistoryState });
        const { getMedicalHistoryConfigData } = this;
        getMedicalHistoryConfigData();
    }

    componentDidUpdate(prevProps: Readonly<MedicalHistoryCardPadV2Props>): void {
        if (prevProps.rxDocId !== this.props.rxDocId) {
            this.getMedicalHistoryConfigData();
        }
    }

    getMedicalHistoryConfigData = () => {
        const { rxDocId } = this.props;
        const myId = getMyIdWithBackwardCompatibility() || '';
        const currentUserId = rxDocId || myId || '';
        getItemFromMisc<MedicalHistoryConfig>(currentUserId, MISC_KEYS.medicalHistoryConfig)
            .then((medicalHistoryConfigData) => {
                this.setState({
                    medicalHistoryConfigData: medicalHistoryConfigData,
                });
            })
            .catch(console.error);
    };

    setIsOpen = () => {
        const { dispatch } = this.props;
        dispatch(upsertMedicalHistoryCardState(!this.state.isOpen));
        this.setState({ isOpen: !this.state.isOpen });
    };
    setShowMedicalHistoryDetails = (isShow: boolean) => {
        const { dispatch } = this.props;
        this.setState({ showMedicalHistoryDetails: isShow });
        dispatch(upsertMedicalHistoryCardState(isShow));
        this.setState({ isOpen: isShow });
        dispatch(toggleNoKnownMedicalHistory(!isShow));
        !isShow && dispatch(clearMedicalHistory());
    };

    onItemSave = (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number): void => {
        const { dispatch } = this.props;
        const { status } = item;
        if (status) {
            catId && dispatch(upsertMedicalHistory(catId, item, index));
        } else {
            catId && dispatch(upsertMedicalHistory(catId, { ...item, status: '' }, index));
        }
    };
    onItemDelete = (catId: MEDICAL_HISTORY_CATEGORY, itemId: string): void => {
        const { dispatch } = this.props;
        dispatch(deleteMedicalHistory(catId, itemId));
    };
    onItemSwap = (catId: MEDICAL_HISTORY_CATEGORY, oldIndex: number, newIndex: number): void => {
        if (oldIndex !== newIndex) {
            this.props.dispatch(swapMedicalHistoryElement(catId, oldIndex, newIndex));
        }
    };
    renderLeftHeader = () => {
        const { isOpen, showMedicalHistoryDetails } = this.state;
        const { isMedicalHistorySection, sectionName } = this.props;
        return !showMedicalHistoryDetails || isMedicalHistorySection ? null : (
            <div
                className="flex justify-center items-center cursor-pointer rounded-full bg-neutral-9 border border-darwin-neutral-100 min-h-24 min-w-24"
                onClick={this.setIsOpen}
            >
                <LeftHeaderArrow isOpen={isOpen} />
            </div>
        );
    };
    renderRightHeader = () => {
        return (
            <div>
                <MedicalHistoryHeaderOptions
                    setShowMedicalHistory={this.setShowMedicalHistoryDetails}
                    showMedicalHistory={this.state.showMedicalHistoryDetails}
                    onRefresh={this.getMedicalHistoryConfigData}
                />
            </div>
        );
    };

    render(): React.ReactNode {
        const { isMedicalHistorySection, medicalHistory, sectionName } = this.props;
        const { showMedicalHistoryDetails, isOpen, medicalHistoryConfigData } = this.state;
        return (
            <MedicalHistoryCommonUi
                renderLeftHeader={this.renderLeftHeader}
                renderRightHeader={this.renderRightHeader}
                onItemDelete={this.onItemDelete}
                onItemSave={this.onItemSave}
                onItemSwap={this.onItemSwap}
                isMedicalHistorySection={isMedicalHistorySection}
                medicalHistory={medicalHistory}
                setShowMedicalHistoryDetails={this.setShowMedicalHistoryDetails}
                showMedicalHistoryDetails={showMedicalHistoryDetails}
                medicalHistoryConfigData={medicalHistoryConfigData}
                isOpen={isOpen}
                sectionName={sectionName}
                onRefresh={this.getMedicalHistoryConfigData}
            />
        );
    }
}

export default connect((state: GlobalState) => ({
    medicalHistory: getPatientMedicalHistory(state),
    cardState: getMedicalHistoryCardState(state),
    noKnownMedicalHistoryState: getNoKnownMedicalHistoryState(state),
    rxDocId: getPrescription(state)?.doctor?._id || '',
}))(MedicalHistoryCardPadV2);

type MedicalHistoryPanelState = {
    showMedicalHistoryDetails: boolean;
    isOpen: boolean;
};

interface MedicalHistoryPanelProps {
    isMedicalHistorySection?: boolean;
    onItemsSaveToDb: () => void;
    onItemSaveInState: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    onItemDeleteFromState: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    onClearMedItem: () => void;
    onSwapItem: (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => void;
    medicalHistory: MHC;
    fetchState?: 'error' | 'success';
    medHistorySaveState: 'loading' | 'success' | 'changed' | undefined;
    setMedHistorySaveState: (state?: 'loading' | 'success' | 'changed' | undefined) => void;
    medicalHistoryConfigData: MedicalHistoryConfig;
    onRefresh: () => void;
}

export class MedicalHistoryPanelComponent extends PureComponent<
    MedicalHistoryPanelProps,
    MedicalHistoryPanelState
> {
    state: MedicalHistoryPanelState = {
        showMedicalHistoryDetails: true,
        isOpen: true,
    };

    setShowMedicalHistoryDetails = (isShow: boolean) => {
        const { onClearMedItem } = this.props;
        this.props.setMedHistorySaveState('changed');
        this.setState({ showMedicalHistoryDetails: isShow });
        this.setState({ isOpen: isShow });
        !isShow && onClearMedItem();
    };
    onItemSave = (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number): void => {
        const { onItemSaveInState } = this.props;
        this.props.setMedHistorySaveState('changed');
        const { status } = item;
        if (status) {
            catId && onItemSaveInState(catId, item, index);
        } else {
            catId && onItemSaveInState(catId, { ...item, status: 'Active' }, index);
        }
    };
    onItemDelete = (catId: MEDICAL_HISTORY_CATEGORY, itemId: string): void => {
        const { onItemDeleteFromState } = this.props;
        this.props.setMedHistorySaveState('changed');
        onItemDeleteFromState(catId, itemId);
    };
    onItemSwap = (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => {
        const { onSwapItem } = this.props;
        this.props.setMedHistorySaveState('changed');
        onSwapItem(catId, from, to);
    };
    renderRightHeader = () => {
        const { onRefresh } = this.props;
        return (
            <div className={'mt-8'}>
                <MedicalHistoryHeaderOptions
                    setShowMedicalHistory={this.setShowMedicalHistoryDetails}
                    showMedicalHistory={this.state.showMedicalHistoryDetails}
                    onRefresh={onRefresh}
                />
            </div>
        );
    };

    render(): React.ReactNode {
        const {
            isMedicalHistorySection,
            medicalHistory,
            medicalHistoryConfigData,
            onRefresh,
        } = this.props;
        const { showMedicalHistoryDetails, isOpen } = this.state;

        return (
            <MedicalHistoryCommonUi
                renderRightHeader={this.renderRightHeader}
                onItemDelete={this.onItemDelete}
                onItemSave={this.onItemSave}
                onItemSwap={this.onItemSwap}
                isMedicalHistorySection={isMedicalHistorySection}
                medicalHistory={medicalHistory}
                setShowMedicalHistoryDetails={this.setShowMedicalHistoryDetails}
                showMedicalHistoryDetails={showMedicalHistoryDetails}
                medicalHistoryConfigData={medicalHistoryConfigData}
                onSave={this.props.onItemsSaveToDb}
                isPhSection={true}
                medHistorySaveState={this.props.medHistorySaveState}
                isOpen={isOpen}
                onRefresh={onRefresh}
            />
        );
    }
}

interface MedicalHistoryCommonUiProps {
    renderLeftHeader?: () => JSX.Element | null;
    renderRightHeader: () => JSX.Element | null;
    onItemDelete: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    onItemSwap: (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => void;
    setShowMedicalHistoryDetails: (isShow: boolean) => void;
    onSave?: (() => void) | undefined;
    medHistorySaveState?: 'loading' | 'success' | 'changed' | undefined;
    medicalHistoryConfigData: MedicalHistoryConfig;
    showMedicalHistoryDetails: boolean;
    isMedicalHistorySection?: boolean;
    medicalHistory?: MHC;
    isPhSection?: boolean;
    isOpen: boolean;
    sectionName?: string;
    onRefresh: () => void;
}

export const OpenCardSections: React.FC<{
    medicalHistoryConfigData: MedicalHistoryConfig;
    isMedicalHistorySection?: boolean;
    onItemSave: (catId: MEDICAL_HISTORY_CATEGORY, item: MHItem2, index?: number) => void;
    onItemDelete: (catId: MEDICAL_HISTORY_CATEGORY, itemId: string) => void;
    onItemSwap: (catId: MEDICAL_HISTORY_CATEGORY, from: number, to: number) => void;
    medicalHistory?: MHC;
    showMedicalHistoryDetails: boolean;
    isOpen: boolean;
    onConfigure?: () => void;
    onRefresh: () => void;
}> = ({
    medicalHistoryConfigData,
    onItemSave,
    onItemDelete,
    onItemSwap,
    isMedicalHistorySection,
    medicalHistory,
    showMedicalHistoryDetails,
    isOpen,
    onConfigure,
    onRefresh,
}) => {
    const animationTime = useRef(0);

    useEffect(() => {
        setTimeout(() => {
            animationTime.current = 0.4;
        }, 200);
    }, []);

    if (
        !(
            medicalHistoryConfigData?.commonItems?.length ||
            medicalHistoryConfigData?.sections?.length
        )
    ) {
        return <EmptyMedicalHistoryMessageUI />;
    }

    return (
        <Fragment>
            <AnimationContainer
                id={'mh-container'}
                delay={animationTime.current}
                isOpen={showMedicalHistoryDetails && isMedicalHistorySection ? true : isOpen}
            >
                <Fragment>
                    <MedicalHistoryItemsPadV2
                        medicalHistoryConfigData={medicalHistoryConfigData}
                        isMedicalHistorySection={isMedicalHistorySection}
                        onItemSave={onItemSave}
                        onItemDelete={onItemDelete}
                        onItemSwap={onItemSwap}
                        patientHistoryData={medicalHistory}
                        showMedicalHistoryDetails={showMedicalHistoryDetails}
                        onConfigure={onConfigure}
                        onRefresh={onRefresh}
                    />
                </Fragment>
            </AnimationContainer>
            <AnimationContainer
                delay={
                    animationTime.current <= 0 ? animationTime.current : animationTime.current - 0.2
                }
                isOpen={showMedicalHistoryDetails && isMedicalHistorySection ? false : !isOpen}
            >
                <CardCloseMessage
                    showMedicalHistoryDetails={showMedicalHistoryDetails}
                    medicalHistory={medicalHistory}
                />
            </AnimationContainer>
        </Fragment>
    );
};

const CardCloseMessage: React.FC<{
    showMedicalHistoryDetails: boolean;
    medicalHistory?: MHC;
}> = ({ showMedicalHistoryDetails, medicalHistory }): JSX.Element => {
    if (!showMedicalHistoryDetails) {
        return (
            <p className="text-darwin-neutral-600 font-400 text-14 select-none mt-16">
                Uncheck “no known medical history” to add patient medical history
            </p>
        );
    }

    return (
        <div key="content-1" className="mt-16">
            <MedicalHistorySummary {...medicalHistory} />
        </div>
    );
};

export const AnimationContainer: React.FC<{
    id?: string;
    delay: number;
    children: JSX.Element;
    isOpen: boolean;
}> = ({ id, delay, children, isOpen }) => {
    useEffect(() => {
        setTimeout(() => {
            const dom = id ? document.getElementById(id) : undefined;
            if (id && dom && isOpen) {
                dom.style.overflow = '';
            }
        }, delay * 1000 + 200);
    });

    return (
        <motion.div
            id={id || undefined}
            variants={{
                open: {
                    opacity: 1,
                    height: 'auto',
                    overflow: 'auto',
                    transition: {
                        duration: delay,
                    },
                },
                collapsed: {
                    opacity: 0,
                    height: 0,
                    zIndex: 100,
                    overflow: 'auto',
                    transition: {
                        duration: delay,
                    },
                },
            }}
            animate={isOpen ? 'open' : 'collapsed'}
        >
            {children}
        </motion.div>
    );
};

class MedicalHistoryCommonUi extends PureComponent<MedicalHistoryCommonUiProps> {
    closeMe = () => {
        history.back();
    };

    render(): React.ReactNode {
        const {
            showMedicalHistoryDetails,
            onSave,
            medHistorySaveState,
            isPhSection,
            renderLeftHeader,
            renderRightHeader,
            isMedicalHistorySection,
            sectionName,
        } = this.props;
        return (
            <PadV2PxElementCard
                icon={MedicalHistoryIcon}
                headingText={sectionName || 'Patient Medical History'}
                iconContainerBgClassName="bg-darwin-accent-medical-history-orange-900"
                renderHeaderLeft={renderLeftHeader}
                renderHeaderRight={renderRightHeader}
                bodyCssClassName={
                    isMedicalHistorySection ? 'rounded-0 mt-40 bg-neutral-8' : undefined
                }
                headerCssClassName={
                    isMedicalHistorySection
                        ? 'absolute top-0 left-0 right-0 px-16 bg-white border-b-1 border-neutral-8 py-8'
                        : undefined
                }
            >
                <div
                    id={MEDICAL_HISTORY_CONTAINER}
                    className={`${isMedicalHistorySection ? 'overflow-auto relative' : 'h-full'}
                        ${showMedicalHistoryDetails ? 'h-full' : 'pointer-events-none h-auto'}
                        row-container`}
                >
                    <div
                        className={
                            isMedicalHistorySection
                                ? 'h-auto rounded-8 bg-white p-16 pt-0 mt-16'
                                : ''
                        }
                    >
                        <OpenCardSections {...this.props} />
                    </div>
                    {isMedicalHistorySection && <div className="h-253"></div>}
                </div>
                {isPhSection && (
                    <div className="flex items-center justify-between absolute rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row">
                        <button
                            onClick={this.closeMe}
                            className="text-btn-v2 px-16 py-8 btn--small"
                        >
                            Go Back
                        </button>
                        <button
                            className="px-16 py-8 btn--small primary-btn-v2 tooltip-wrapper"
                            onClick={onSave}
                        >
                            {(medHistorySaveState === 'changed' ||
                                medHistorySaveState === undefined) && (
                                <div className="flex items-center justify-center w-16 h-16">
                                    <svg
                                        className="w-16 h-16 fill-actionPrimary"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                    >
                                        <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                                    </svg>
                                </div>
                            )}

                            {medHistorySaveState === 'loading' && (
                                <div className="w-20 h-20">
                                    <svg className="circular-loader" viewBox="25 25 50 50">
                                        <circle
                                            className="loader-path"
                                            cx="50"
                                            cy="50"
                                            r="20"
                                            fill="none"
                                            stroke="#004BA8"
                                            stroke-width="3"
                                        ></circle>
                                    </svg>
                                </div>
                            )}
                            {medHistorySaveState === 'success' && (
                                <div className="flex items-center justify-center">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#004BA8">
                                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"></path>
                                    </svg>
                                </div>
                            )}
                            <p>
                                {' '}
                                {medHistorySaveState == 'success'
                                    ? 'Changes Saved'
                                    : 'Save Changes'}
                            </p>
                        </button>
                    </div>
                )}
            </PadV2PxElementCard>
        );
    }
}

export const LeftHeaderArrow = ({ isOpen }: { isOpen: boolean }): JSX.Element => {
    return isOpen
        ? topArrowIcon()
        : bottonArrowIcon('fill-darwin-accent-medical-history-orange-900');
};

export const MedicalHistoryHeaderOptions: React.FC<{
    setShowMedicalHistory: (val: boolean) => void;
    showMedicalHistory: boolean;
    onItemsSaveToDb?: () => void;
    medHistorySaveState?: 'loading' | 'success' | 'changed';
    onRefresh: () => void;
}> = ({ setShowMedicalHistory, showMedicalHistory, onRefresh }) => {
    const [historyConfigDialogVisibility, setHistoryConfigDialogVisibility] = useState(false);

    function openHistoryConfigDialog() {
        setHistoryConfigDialogVisibility(true);
    }

    function closeHistoryConfigDialog() {
        setHistoryConfigDialogVisibility(false);
        onRefresh();
    }

    return (
        <Fragment>
            <AnimatePresence>
                {historyConfigDialogVisibility && (
                    <PPMedicalHistoryConfigPanel closeMe={closeHistoryConfigDialog} />
                )}
            </AnimatePresence>
            <div className="flex items-center space-x-16 w-full">
                <div
                    className="flex items-center space-x-8 w-full"
                    onClick={() => {
                        setShowMedicalHistory(!showMedicalHistory);
                    }}
                >
                    <div className="cursor-pointer">
                        {showMedicalHistory ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-neutral-6"
                            >
                                <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-darwin-primary-500"
                            >
                                <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"></path>
                            </svg>
                        )}
                    </div>
                    <p
                        className={`body-1 bold text-darwin-neutral-1000 whitespace-nowrap cursor-pointer select-none`}
                    >
                        No known medical history
                    </p>
                </div>

                <button
                    onClick={openHistoryConfigDialog}
                    className="secondary-btn px-8 py-4 body-2 space-x-8 font-400"
                >
                    <span className="whitespace-nowrap select-none">Configure</span>
                    <svg
                        width="14"
                        height="15"
                        viewBox="0 0 14 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M13.6445 9.82812C13.6445 9.69141 13.5625 9.52734 13.4258 9.44531L12.1406 8.73438C12.1953 8.40625 12.25 8.07812 12.25 7.75C12.25 7.44922 12.1953 7.12109 12.1406 6.79297L13.4258 6.08203C13.5625 6 13.6445 5.83594 13.6445 5.67188C13.6445 5.28906 12.4961 2.96484 11.8945 2.96484C11.8125 2.96484 11.7305 2.99219 11.6758 3.04688L10.3906 3.78516C9.89844 3.34766 9.35156 3.04688 8.75 2.82812V1.35156C8.75 1.13281 8.58594 0.941406 8.39453 0.914062C7.92969 0.804688 7.46484 0.75 7 0.75C6.50781 0.75 6.04297 0.804688 5.57812 0.914062C5.38672 0.96875 5.25 1.13281 5.25 1.35156V2.82812C4.62109 3.04688 4.07422 3.34766 3.58203 3.78516L2.29688 3.04688C2.24219 2.99219 2.16016 2.96484 2.07812 2.96484C1.53125 2.96484 0.328125 5.20703 0.328125 5.67188C0.328125 5.83594 0.410156 6 0.546875 6.08203L1.83203 6.79297C1.77734 7.12109 1.75 7.42188 1.75 7.75C1.75 8.07812 1.77734 8.40625 1.83203 8.73438L0.546875 9.44531C0.410156 9.52734 0.328125 9.69141 0.328125 9.82812C0.328125 10.2383 1.47656 12.5625 2.07812 12.5625C2.16016 12.5625 2.24219 12.5352 2.29688 12.4805L3.58203 11.7422C4.07422 12.1523 4.62109 12.4805 5.25 12.6992V14.1758C5.25 14.3945 5.38672 14.5586 5.57812 14.6133C6.04297 14.6953 6.50781 14.75 6.97266 14.75C7.41016 14.75 7.90234 14.7227 8.36719 14.6133C8.58594 14.5586 8.75 14.3945 8.75 14.1758V12.6992C9.35156 12.4805 9.89844 12.1523 10.3906 11.7422L11.6758 12.4805C11.7305 12.5352 11.8125 12.5625 11.8945 12.5625C12.4414 12.5352 13.6445 10.3203 13.6445 9.82812ZM7 9.9375C5.76953 9.9375 4.8125 8.98047 4.8125 7.75C4.8125 6.54688 5.76953 5.5625 7 5.5625C8.20312 5.5625 9.1875 6.54688 9.1875 7.75C9.1875 8.98047 8.20312 9.9375 7 9.9375Z"
                            fill="#004BA8"
                        ></path>
                    </svg>
                </button>
            </div>
        </Fragment>
    );
};
